import { v4 } from "uuid";
import { CHESS_SIDE, GAME_RESULT, LEEWAY_DURATION, MATCH_STATUS, } from "./constants";
import { isNumeric } from "./utils/number-helper";
import { GameDataBuilder } from "./GameDataBuilder";
import { diffIn, toDateWithOffset } from "./utils/date-helper";
import { hasCommenced, hasCommencedByMoveCount } from "./utils/match-helper";
export class GameRefereeBase {
    matchId = "";
    match = null;
    moves = [];
    engineInstance = this.engine();
    timer;
    firestoreTimeOffset = 0;
    engine() {
        throw new Error("unimplemented, override the Chess.js instance of platform");
    }
    firestore() {
        throw new Error("unimplemented, override the Firestore instance of platform");
    }
    serverTimestamp() {
        throw new Error("unimplemented, override serverTimestamp base on platform");
    }
    async setSchedule(name, color, seconds) {
        console.log(name, color, seconds);
        throw new Error("unimplemented, override setSchedule base on platform");
    }
    async clearSchedules() {
        throw new Error("unimplemented, override clearSchedules base on platform");
    }
    async getTimeOffset() {
        const ref = this.firestore().collection("temps").doc(v4());
        await ref.set({
            localTimestamp: new Date(),
            serverTimestamp: this.serverTimestamp(),
        });
        const doc = await ref.get();
        const data = doc.data();
        const firestoreTimeOffset = data?.serverTimestamp - data?.localTimestamp;
        console.log("game referee's firestoreTimeOffset", firestoreTimeOffset);
        if (!isNumeric(firestoreTimeOffset))
            return 0;
        return Number(firestoreTimeOffset);
    }
    async init(matchId) {
        this.matchId = matchId;
        this.firestoreTimeOffset = await this.getTimeOffset();
        await this.fetchData();
    }
    reset() { }
    async fetchData() {
        this.moves = [];
        this.match = null;
        const matchRef = this.firestore().collection("matches").doc(this.matchId);
        const movesRef = matchRef.collection("moves");
        this.match = (await matchRef.get()).data();
        const moves = (await movesRef.get()).docs;
        moves.forEach((doc) => {
            this.moves.push({
                id: doc.id,
                ...doc.data(),
            });
        });
        await this.loadEngine();
    }
    setMatchId(matchId) {
        this.matchId = matchId;
    }
    async loadEngine() {
        const lastMove = this.lastMove();
        if (lastMove) {
            // FIXME: temp fix by replacing the first full Move index with 1. so that game.js can load pgn
            this.engineInstance.load_pgn(lastMove.pgn.replace(/\n\n\d+\.\s/, "\n1. "));
            // this.engineInstance.load_pgn(lastMove.pgn);
        }
        else {
            this.engineInstance.load(this.match.initial_fen);
        }
        if (this.isGameOver()) {
            await this.handleGameOver();
        }
    }
    lastMove() {
        return this.moves.length ? this.moves[this.moves.length - 1] : null;
    }
    lastMoves() {
        const lastMove = this.moves.at(-1);
        const beforeLastMove = this.moves.at(-2);
        return {
            [lastMove?.color]: lastMove,
            [beforeLastMove?.color]: beforeLastMove,
        };
    }
    leewayDuration() {
        return LEEWAY_DURATION;
    }
    timeoutLeeway() {
        return 0;
    }
    leewayTimeLeft() {
        const lastMove = this.lastMove();
        const lastMoveAt = lastMove ? lastMove.createdAt : null;
        const leewayCheckedAt = lastMoveAt ||
            this.match?.startedAt ||
            this.match?.createdAt ||
            toDateWithOffset(this.firestoreTimeOffset);
        const leewayElapsed = diffIn(toDateWithOffset(this.firestoreTimeOffset), leewayCheckedAt, "second");
        return this.leewayDuration() - leewayElapsed;
    }
    blackToStart() {
        return this.match?.initial_fen && this.match.initial_fen.includes(" b ")
            ? 1
            : 0;
    }
    turnColor() {
        return this.engineInstance.turn() === "w"
            ? CHESS_SIDE.WHITE
            : CHESS_SIDE.BLACK;
    }
    nextColor() {
        return this.engineInstance.turn() === "w"
            ? CHESS_SIDE.BLACK
            : CHESS_SIDE.WHITE;
    }
    resultText() {
        if (!this.engineInstance.game_over()) {
            return null;
        }
        if (this.engineInstance.in_checkmate()) {
            return GAME_RESULT.CHECKMATE;
        }
        if (this.engineInstance.in_stalemate()) {
            return GAME_RESULT.STALEMATE;
        }
        if (this.engineInstance.in_threefold_repetition()) {
            return GAME_RESULT.THREEFOLD_REPETITION;
        }
        if (this.engineInstance.insufficient_material()) {
            return GAME_RESULT.INSUFFICIENT_MATERIAL;
        }
        if (this.engineInstance.in_draw()) {
            return GAME_RESULT.DRAW;
        }
        return GAME_RESULT.GAME_OVER;
    }
    pieces(color) {
        const rowsFen = this.engineInstance.fen().split(" ")[0];
        if (!rowsFen)
            return [];
        if (color === CHESS_SIDE.WHITE) {
            return rowsFen.match(/[KQRBNP]/g) || [];
        }
        else if (color === CHESS_SIDE.BLACK) {
            return rowsFen.match(/[kqrbnp]/g) || [];
        }
        return [];
    }
    isBareKing(color) {
        return this.pieces(color).length === 1;
    }
    isGameOver() {
        return this.engineInstance.game_over();
    }
    abortAfterLeeway() {
        return !this.match.tournamentId;
    }
    actualTimeLeft(color) {
        const gameDataBuilder = new GameDataBuilder(this.match, this.moves, this.firestoreTimeOffset);
        const timeDataByColors = gameDataBuilder.timeData();
        const timeData = timeDataByColors[color];
        const { timeLeft } = timeData;
        // to account for reload mid game or network delay
        const lastMove = this.lastMove();
        const lastMoveAt = lastMove ? lastMove.createdAt : null;
        const timeLeftCheckedAt = lastMoveAt ||
            this.match.commencedAt ||
            toDateWithOffset(this.firestoreTimeOffset);
        const timeLeftElapsed = this.turnColor() === color
            ? diffIn(toDateWithOffset(this.firestoreTimeOffset), timeLeftCheckedAt, "second")
            : 0;
        return timeLeft - timeLeftElapsed;
    }
    willScheduleTimeout() {
        return true;
    }
    async handleMove() {
        console.debug("handleMove");
        const lastMove = this.lastMove();
        // move's createdAt field using value: firestore.serverTimestamp(),
        // which will be null at creation, so because we calculate moveStats, etc... using createdAt
        // it will not work correctly when createdAt is null,
        // so when createdAt is available later, we will handle the update
        if (!lastMove || !lastMove.createdAt) {
            return;
        }
        const pgn = lastMove.pgn;
        this.engineInstance.load_pgn(pgn);
        await this.checkCommence();
        if (this.isGameOver()) {
            await this.handleGameOver();
        }
        else {
            await this.checkAbortOrTimeout();
        }
    }
    // Commence is when it's a tournament game and the second move has been made
    // or it's not a tournament game and any side made a move
    async checkCommence() {
        if ((this.abortAfterLeeway() && this.moves.length === 2) ||
            (!this.abortAfterLeeway() && this.moves.length === 1)) {
            await this.handleCommence();
        }
    }
    async checkMatchUserAction() {
        if (this.match?.result)
            return;
        if (!this.match?.userAction) {
            return;
        }
        const { userAction } = this.match;
        const { resignedByColor, abortedByColor } = userAction;
        if (resignedByColor) {
            await this.saveMatchResult(resignedByColor === CHESS_SIDE.WHITE ? 0 : 1, resignedByColor === CHESS_SIDE.WHITE ? 1 : 0, GAME_RESULT.RESIGN);
        }
        if (abortedByColor) {
            await this.saveMatchResult(null, null, GAME_RESULT[abortedByColor.toUpperCase() + "_ABORT"]);
        }
    }
    async checkLastMoveUserAction() {
        const lastMove = this.lastMove();
        if (!lastMove)
            return;
        const { userAction } = lastMove;
        if (!userAction) {
            return;
        }
        const { drawResponse } = userAction;
        if (drawResponse === "accept") {
            await this.saveMatchResult(0.5, 0.5, GAME_RESULT.DRAW_BY_AGREEMENT);
        }
    }
    async checkAbortOrTimeout() {
        await this.clearSchedules();
        // match has ended, no move can be made
        if (this.match?.result) {
            console.debug(`Match has already ended with result: ${this.match.result}`);
            return;
        }
        // check abort or commence if match has not commenced
        if (!hasCommenced(this.match, this.moves)) {
            await this.checkAbortOrCommence();
            return;
        }
        // basically only check timeout if match has been commenced
        // match is actually played, so check game timeout
        await this.checkTimeout();
    }
    async checkAbortOrCommence() {
        const lastMove = this.lastMove();
        if (lastMove) {
            console.debug(lastMove.color, lastMove.move, ". Check abort or commence ================");
        }
        // to account for reload mid game or network delay
        const leewayTimeLeft = this.leewayTimeLeft();
        const turnColor = this.turnColor();
        if (leewayTimeLeft <= 0) {
            console.debug(`leewayTimeLeft ${leewayTimeLeft} < 0, ${turnColor} handle abort or commence`);
            await this.handleAbortOrCommence(turnColor);
            return;
        }
        const waitTime = leewayTimeLeft + (this.abortAfterLeeway() ? this.timeoutLeeway() : 0);
        console.debug(`${turnColor} set leeway timer to handle ${this.abortAfterLeeway() ? "abort" : "commence"} | ${waitTime}s`);
        await this.setSchedule("handleLeewayOver", turnColor, waitTime);
    }
    async checkTimeout() {
        if (!this.match)
            return;
        // there is no timeout when duration is not set
        if (!(this.match.duration > 0)) {
            return;
        }
        const gameDataBuilder = new GameDataBuilder(this.match, this.moves, this.firestoreTimeOffset);
        const movesStats = gameDataBuilder.movesStats();
        const lastMoveStats = movesStats.length
            ? movesStats[movesStats.length - 1]
            : null;
        if (lastMoveStats && lastMoveStats.timeLeft <= 0) {
            console.debug(`${lastMoveStats.color} made an invalid move.`, `timeLeft ${lastMoveStats.timeLeft} < 0.`, `${lastMoveStats.color} should have timed out`);
            await this.handleTimeout(lastMoveStats.color);
            return;
        }
        if (lastMoveStats) {
            console.debug(lastMoveStats.color, lastMoveStats.move, ". Check match timeout ================");
        }
        const turnColor = this.turnColor();
        const actualTimeLeft = this.actualTimeLeft(turnColor);
        if (actualTimeLeft <= 0) {
            console.debug(`timeLeft ${actualTimeLeft} < 0, ${turnColor} timed out`);
            this.handleTimeoutOrForfeit(turnColor);
            return;
        }
        if (!this.willScheduleTimeout()) {
            return;
        }
        // Timeout
        console.debug(`${turnColor} set match timeout timer | ${actualTimeLeft}s`);
        await this.setSchedule("handleMatchTimeout", turnColor, actualTimeLeft + this.timeoutLeeway());
    }
    async handleAbortOrCommence(color) {
        if (this.abortAfterLeeway()) {
            await this.handleAbort(color);
        }
        else {
            await this.handleCommence();
        }
    }
    async handleCommence() {
        console.debug("Match commenced at", toDateWithOffset(this.firestoreTimeOffset));
        await this.updateMatch({
            commencedAt: this.serverTimestamp(),
        });
    }
    async handleAbort(color) {
        console.debug(color, "handleAbort");
        if (this.turnColor() != color) {
            console.debug("handleAbort after moved, so skip");
            return;
        }
        console.debug("handleAbort result saved");
        await this.saveMatchResult(null, null, GAME_RESULT.ABORT);
    }
    async handleTimeoutOrForfeit(turnColor) {
        if (hasCommencedByMoveCount(this.match, this.moves)) {
            await this.handleTimeout(turnColor);
        }
        else {
            await this.handleForfeit(turnColor);
        }
    }
    async handleTimeout(color) {
        console.debug(color, "handleTimeout");
        if ((color === CHESS_SIDE.WHITE && this.isBareKing(CHESS_SIDE.BLACK)) ||
            (color === CHESS_SIDE.BLACK && this.isBareKing(CHESS_SIDE.WHITE))) {
            console.debug(color, "timed out. But draw by bare king");
            await this.saveMatchResult(0.5, 0.5, GAME_RESULT.BARE_KING);
            return;
        }
        if (color === CHESS_SIDE.WHITE) {
            console.debug(color, "timed out. Black win");
            await this.saveMatchResult(0, 1, GAME_RESULT.TIMEOUT);
        }
        else if (color === CHESS_SIDE.BLACK) {
            console.debug(color, "timed out. White win");
            await this.saveMatchResult(1, 0, GAME_RESULT.TIMEOUT);
        }
    }
    async handleForfeit(color) {
        console.debug(color, "handleForfeit");
        let whiteScore = 0, blackScore = 0;
        if (color === CHESS_SIDE.WHITE)
            blackScore = 1;
        if (color === CHESS_SIDE.BLACK)
            whiteScore = 1;
        await this.saveMatchResult(whiteScore, blackScore, GAME_RESULT.FORFEIT);
    }
    async handleGameOver() {
        if (this.match?.result)
            return;
        const resultText = this.resultText();
        const turnColor = this.turnColor();
        let whiteScore = 0;
        let blackScore = 0;
        switch (resultText) {
            case GAME_RESULT.CHECKMATE:
                if (turnColor === CHESS_SIDE.BLACK)
                    whiteScore = 1;
                else if (turnColor === CHESS_SIDE.WHITE)
                    blackScore = 1;
                break;
            case GAME_RESULT.STALEMATE:
            case GAME_RESULT.THREEFOLD_REPETITION:
            case GAME_RESULT.INSUFFICIENT_MATERIAL:
            case GAME_RESULT.DRAW:
            case GAME_RESULT.GAME_OVER:
                whiteScore = 0.5;
                blackScore = 0.5;
                break;
            default:
                console.error("invalid game result", resultText);
                return;
        }
        await this.saveMatchResult(whiteScore, blackScore, resultText);
    }
    async saveMatchResult(whiteScore, blackScore, result) {
        const lastMove = this.lastMove();
        // clear timer if there is
        await this.clearSchedules();
        await this.updateMatch({
            result: result,
            player_1_score: whiteScore,
            player_2_score: blackScore,
            status: result === GAME_RESULT.ABORT
                ? MATCH_STATUS.ABORTED
                : MATCH_STATUS.FINISHED,
            pgn: lastMove ? lastMove.pgn || "" : "",
            endedBy: lastMove ? lastMove.player_id : "",
            endedAt: this.serverTimestamp(),
        });
    }
    async updateMatch(payload) {
        const matchRef = this.firestore().collection("matches").doc(this.matchId);
        await matchRef.update(payload);
    }
}
